/**
 * Turning raw audio into a number the orb can use.
 *
 * Two separate jobs, kept apart so the maths can be tested without a browser:
 *   `rmsToUnit`: one frame of samples to a perceptual 0..1
 *   `LevelSmoother`: a sequence of those into something that does not flicker
 *
 * Raw RMS looks wrong on screen. Speech has gaps between syllables, so an
 * unsmoothed meter strobes; and loudness is logarithmic, so a linear meter sits
 * near zero for normal speech and only moves when someone shouts.
 */

/** Below this, treat it as silence. Roughly a quiet room on a phone mic. */
export const NOISE_FLOOR_DB = -60
/** Above this, treat it as full scale. Comfortable speaking distance. */
export const CEILING_DB = -12

export interface LevelSmootherOptions {
  /** How fast the meter rises. Higher is snappier. 0..1 per frame. */
  attack?: number
  /** How fast it falls. Lower leaves a tail so syllable gaps do not strobe. */
  release?: number
  /** Frames of stillness before the value is pinned to exactly 0. */
  silenceFrames?: number
}

/** Root mean square of a frame of float samples in -1..1. */
export function rms(samples: Float32Array | readonly number[]): number {
  const length = 'length' in samples ? samples.length : 0
  if (length === 0) return 0
  let sum = 0
  for (let i = 0; i < length; i += 1) {
    const sample = samples[i] as number
    sum += sample * sample
  }
  return Math.sqrt(sum / length)
}

/** Maps an RMS amplitude to 0..1 through decibels, so it tracks perception. */
export function rmsToUnit(value: number, floorDb = NOISE_FLOOR_DB, ceilingDb = CEILING_DB): number {
  if (value <= 0) return 0
  const db = 20 * Math.log10(value)
  if (db <= floorDb) return 0
  if (db >= ceilingDb) return 1
  return (db - floorDb) / (ceilingDb - floorDb)
}

/** Convenience: a frame of samples straight to a 0..1 level. */
export function frameToLevel(samples: Float32Array | readonly number[]): number {
  return rmsToUnit(rms(samples))
}

/**
 * Asymmetric smoothing. Rises quickly so the orb answers the voice without
 * lag, falls slowly so it does not flicker between syllables.
 */
export class LevelSmoother {
  private value = 0
  private quietFrames = 0
  private readonly attack: number
  private readonly release: number
  private readonly silenceFrames: number

  constructor(options: LevelSmootherOptions = {}) {
    this.attack = options.attack ?? 0.45
    this.release = options.release ?? 0.12
    this.silenceFrames = options.silenceFrames ?? 12
  }

  push(target: number): number {
    const clamped = Math.min(1, Math.max(0, target))
    const coefficient = clamped > this.value ? this.attack : this.release
    this.value += (clamped - this.value) * coefficient

    if (clamped < 0.02) {
      this.quietFrames += 1
      if (this.quietFrames >= this.silenceFrames) this.value = 0
    } else {
      this.quietFrames = 0
    }

    // Keep the value from lingering as an unusable fraction.
    if (this.value < 0.001) this.value = 0
    return this.value
  }

  get current(): number {
    return this.value
  }

  reset(): void {
    this.value = 0
    this.quietFrames = 0
  }
}

/**
 * Level tracking with an adaptive noise floor.
 *
 * A fixed floor does not survive contact with real devices. `autoGainControl`
 * is on (it has to be, people hold phones at wildly different distances), and
 * microphone gain varies by more than 20 dB between a laptop and a low-end
 * Android handset. With a fixed floor the orb either sits dead for a softly
 * spoken user or pins wide open in a noisy market.
 *
 * So the floor follows the room: it drops quickly toward whatever quiet
 * sounds like here, and creeps back up slowly so a long silence does not leave
 * the meter hypersensitive to a cough.
 */
export class AdaptiveLevel {
  private floorDb: number
  private level = 0
  private readonly windowDb: number
  private readonly headroomDb: number
  private readonly attackTau: number
  private readonly releaseTau: number
  private readonly curve: number

  constructor(
    options: {
      initialFloorDb?: number
      /** Dynamic range above the floor that maps to 0..1. */
      windowDb?: number
      /** Dead zone just above the floor, so room tone does not register. */
      headroomDb?: number
      /** Seconds for the meter to rise. */
      attackTau?: number
      /** Seconds for the meter to fall. */
      releaseTau?: number
      /** Perceptual exponent: below 1 lifts quiet speech into view. */
      curve?: number
    } = {},
  ) {
    this.floorDb = options.initialFloorDb ?? NOISE_FLOOR_DB
    this.windowDb = options.windowDb ?? 45
    this.headroomDb = options.headroomDb ?? 6
    this.attackTau = options.attackTau ?? 0.05
    this.releaseTau = options.releaseTau ?? 0.25
    this.curve = options.curve ?? 0.65
  }

  /** Feeds one frame. `dt` is seconds since the previous frame. */
  push(samples: Float32Array | readonly number[], dt: number): number {
    const amplitude = rms(samples)
    const db = 20 * Math.log10(amplitude + 1e-9)

    // Fall fast toward quiet, creep up slowly. The asymmetry is the whole point.
    this.floorDb =
      db < this.floorDb ? this.floorDb + (db - this.floorDb) * 0.05 : this.floorDb + 0.15 * dt
    // Never let the floor wander somewhere unusable.
    this.floorDb = Math.min(-25, Math.max(-90, this.floorDb))

    const base = this.floorDb + this.headroomDb
    const top = this.floorDb + this.windowDb
    const target = top <= base ? 0 : Math.min(1, Math.max(0, (db - base) / (top - base)))

    const tau = target > this.level ? this.attackTau : this.releaseTau
    const alpha = Math.exp(-dt / tau)
    this.level = alpha * this.level + (1 - alpha) * target
    if (this.level < 0.004) this.level = 0

    return this.level ** this.curve
  }

  get current(): number {
    return this.level ** this.curve
  }

  get noiseFloorDb(): number {
    return this.floorDb
  }

  reset(): void {
    this.level = 0
    this.floorDb = NOISE_FLOOR_DB
  }
}

/**
 * A speech-shaped level envelope for cases where the real output audio cannot
 * be measured.
 *
 * The browser's `SpeechSynthesis` gives no access to its audio graph, so when
 * the assistant is talking through it there is nothing to analyse. Rather than
 * fake a random wobble, this drives the envelope from the word boundaries the
 * synthesiser does report: the level swells on each word and decays between
 * them, so the orb's rhythm matches the speech even though its amplitude is
 * modelled. Once a real Namu voice streams audio, this is replaced by an
 * analyser on that stream.
 */
export class SpeechEnvelope {
  private level = 0
  private lastWordAt = 0
  private active = false

  /** Call when the synthesiser reports it has reached a word. */
  onWord(wordLength: number, now: number): void {
    this.active = true
    this.lastWordAt = now
    // Longer words carry more energy, but the range stays narrow so the orb
    // reads as speech rather than as a bar chart.
    this.level = Math.min(1, 0.52 + Math.min(wordLength, 12) / 26)
  }

  start(now: number): void {
    this.active = true
    this.lastWordAt = now
    this.level = 0.5
  }

  stop(): void {
    this.active = false
    this.level = 0
  }

  /** Current level, decayed toward a speaking baseline since the last word. */
  sample(now: number): number {
    if (!this.active) return 0
    const elapsed = now - this.lastWordAt
    // ~220ms half-life: the cadence of ordinary speech.
    const decay = Math.exp(-elapsed / 220)
    const baseline = 0.22
    return baseline + (this.level - baseline) * Math.max(0, decay)
  }
}
